/**
 * 「入荷・検品を記録」ダイアログ (2026-10-30〜)。
 * 設計: docs/04_design/fulfillment_inspection_mockup ③ (通常カテゴリー) / ⑦ (TimeWaver)
 *
 * - 入荷日を入れて保存 → 発注が「入荷済（検品待ち）」
 * - 検品済のチェックと、機器ごとの入力がすべてそろって保存 → 「検品済（発送待ち）」
 * - TimeWaver の商談では、機器ごとに付属品チェック・保証期間 (Dell PC はサービスタグ・エクスプレスコード)
 * - 設置日は入れない (納品完了の日が入る)
 * 発送済・キャンセルの発注は見るだけ。
 */
import type {
  FulfillmentDeviceKind,
  InspectionAccessoryKey,
  PurchaseOrderInspection,
  PurchaseOrderInspectionUnit,
} from '@goenhub/shared';
import {
  accessoriesFor,
  defaultWarrantyEnd,
  deviceKindLabel,
  GENERAL_DEVICE_KINDS,
  hasWarranty,
  INSPECTION_ACCESSORY_LABEL,
  inspectionProgress,
  missingUnitFields,
  needsUnits,
  normalizeInspection,
  normalizeInspectionLine,
  PURCHASE_ORDER_STATUS_LABEL,
  serialFieldLabel,
  statusFromInspection,
  TIMEWAVER_DEVICE_KINDS,
} from '@goenhub/shared';
import { Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { FulfillmentSteps } from '../components/FulfillmentSteps';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { useAdminDirectory } from '@/hooks/useAdminDirectory';
import { useAuth } from '@/hooks/useAuth';
import { type PurchaseOrderRecord, useSavePurchaseOrderInspection } from '@/hooks/useSalesOps';
import { PO_STATUS_VARIANT, todayYmd } from '@/lib/purchaseOrderStatus';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  po: PurchaseOrderRecord;
  isTimeWaver: boolean;
  contactName?: string | null;
  /** 保存して「検品済」になったとき、続けて「顧客へ発送」を開く。 */
  onProceedToShip?: () => void;
}

const SELECT_CLASS =
  'h-[34px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-2 text-[13px] text-[var(--text-primary)] disabled:opacity-60';

function initialInspection(
  po: PurchaseOrderRecord,
  isTimeWaver: boolean,
  editable: boolean,
  uid: string | null,
): PurchaseOrderInspection {
  const ins = normalizeInspection(po, isTimeWaver);
  if (!editable) return ins;
  return {
    ...ins,
    arrivedAt: ins.arrivedAt ?? todayYmd(),
    inspectorUid: ins.inspectorUid ?? uid,
  };
}

/** Firestore に undefined を送らないよう、値を整えた保存用のコピー。 */
function cleanForSave(
  ins: PurchaseOrderInspection,
  inspectorName: string | null,
): PurchaseOrderInspection {
  const str = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);
  return {
    arrivedAt: str(ins.arrivedAt),
    inspected: !!ins.inspected,
    inspectedAt: ins.inspected ? (str(ins.inspectedAt) ?? todayYmd()) : str(ins.inspectedAt),
    inspectorUid: ins.inspectorUid ?? null,
    inspectorName,
    memo: str(ins.memo),
    lines: ins.lines.map((line) => ({
      deviceKind: line.deviceKind ?? null,
      units: line.units.map(
        (u): PurchaseOrderInspectionUnit => ({
          serialNumber: (u.serialNumber ?? '').trim(),
          accessories: u.accessories
            ? Object.fromEntries(Object.entries(u.accessories).map(([k, v]) => [k, !!v]))
            : null,
          warrantyStartAt: str(u.warrantyStartAt),
          warrantyEndAt: str(u.warrantyEndAt),
          expressCode: str(u.expressCode),
        }),
      ),
    })),
  };
}

export function PurchaseOrderInspectionDialog({
  open,
  onOpenChange,
  dealId,
  po,
  isTimeWaver,
  contactName,
  onProceedToShip,
}: Props) {
  const { user } = useAuth();
  const directory = useAdminDirectory();
  const save = useSavePurchaseOrderInspection();
  const editable = po.status === 'ordered' || po.status === 'received' || po.status === 'inspected';
  const [ins, setIns] = useState<PurchaseOrderInspection>(() =>
    initialInspection(po, isTimeWaver, editable, user?.uid ?? null),
  );

  // 開いたときだけ読み込み直す (開いている間の再取得で入力中の値を消さない)
  useEffect(() => {
    if (open) setIns(initialInspection(po, isTimeWaver, editable, user?.uid ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const members = useMemo(
    () =>
      (directory.data ?? [])
        .map((m) => ({ uid: m.uid, name: m.displayName || m.fullName || '' }))
        .filter((m) => m.name),
    [directory.data],
  );
  const nameOf = (uid: string | null): string | null => {
    if (!uid) return null;
    const m = members.find((x) => x.uid === uid);
    if (m) return m.name;
    // 名簿に無い (退職等) ときは保存済みの名前を使う
    return uid === po.inspection?.inspectorUid ? (po.inspection?.inspectorName ?? null) : null;
  };

  const kinds: FulfillmentDeviceKind[] = isTimeWaver
    ? TIMEWAVER_DEVICE_KINDS
    : GENERAL_DEVICE_KINDS;
  const progress = inspectionProgress({ lineItems: po.lineItems, inspection: ins }, isTimeWaver);

  const setLineKind = (li: number, kind: FulfillmentDeviceKind) =>
    setIns((prev) => ({
      ...prev,
      lines: prev.lines.map((l, i) =>
        i === li
          ? normalizeInspectionLine(
              po.lineItems[li],
              { deviceKind: kind, units: l.units },
              isTimeWaver,
            )
          : l,
      ),
    }));

  const setUnit = (
    li: number,
    ui: number,
    patch: (u: PurchaseOrderInspectionUnit) => Partial<PurchaseOrderInspectionUnit>,
  ) =>
    setIns((prev) => ({
      ...prev,
      lines: prev.lines.map((l, i) =>
        i === li
          ? { ...l, units: l.units.map((u, k) => (k === ui ? { ...u, ...patch(u) } : u)) }
          : l,
      ),
    }));

  const changeWarrantyStart = (li: number, ui: number, value: string) =>
    setUnit(li, ui, (u) => {
      // 終了日が空、または前の開始日から自動で入れた値のままなら、2 年後の前日を入れ直す
      const autoEnd = u.warrantyStartAt ? defaultWarrantyEnd(u.warrantyStartAt) : null;
      const endIsAuto = !u.warrantyEndAt || u.warrantyEndAt === autoEnd;
      return {
        warrantyStartAt: value,
        ...(endIsAuto ? { warrantyEndAt: value ? defaultWarrantyEnd(value) : null } : {}),
      };
    });

  const toggleAccessory = (li: number, ui: number, key: InspectionAccessoryKey, checked: boolean) =>
    setUnit(li, ui, (u) => ({ accessories: { ...(u.accessories ?? {}), [key]: checked } }));

  // 足りない項目 (フッターに出す)
  const missing: string[] = [];
  if (!progress.arrived) missing.push('入荷日');
  ins.lines.forEach((line, li) => {
    const name = po.lineItems[li]?.name ?? `明細 ${li + 1}`;
    if (line.deviceKind === null) {
      missing.push(`${name} の種類`);
      return;
    }
    line.units.forEach((u, ui) => {
      const m = missingUnitFields(line.deviceKind, u);
      if (m.length > 0) {
        missing.push(`${name}${line.units.length > 1 ? ` #${ui + 1}` : ''} の${m.join('・')}`);
      }
    });
  });
  if (!ins.inspected) missing.push('検品済のチェック');
  const shownMissing = missing.slice(0, 6);
  const moreMissing = missing.length - shownMissing.length;

  const handleSave = async (proceed: boolean) => {
    const toSave = cleanForSave(ins, nameOf(ins.inspectorUid));
    const p = inspectionProgress({ lineItems: po.lineItems, inspection: toSave }, isTimeWaver);
    const status = statusFromInspection(po.status, p);
    try {
      await save.mutateAsync({ dealId, poId: po.id, inspection: toSave, status });
      toast.success(
        status === 'inspected'
          ? '検品済（発送待ち）になりました。顧客へ発送できます。'
          : status === 'received'
            ? '入荷済（検品待ち）として保存しました。'
            : '保存しました。',
      );
      onOpenChange(false);
      if (proceed && status === 'inspected') onProceedToShip?.();
    } catch (err) {
      toast.error(`保存に失敗しました: ${(err as Error).message}`);
    }
  };

  const countLabel = isTimeWaver
    ? `そろった機器 ${progress.completeUnits}/${progress.totalUnits}`
    : `シリアル ${progress.filledSerials}/${progress.totalUnits}`;
  const countDone = progress.totalUnits > 0 && progress.completeUnits === progress.totalUnits;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            入荷・検品を記録
            <Badge variant={PO_STATUS_VARIANT[po.status]}>
              {PURCHASE_ORDER_STATUS_LABEL[po.status]}
            </Badge>
            {isTimeWaver && <Badge>TimeWaver</Badge>}
          </DialogTitle>
          <DialogDescription>
            {contactName ? `${contactName} 様 · ` : ''}
            <span className="font-mono">{po.poNumber}</span>
            {po.supplierName ? ` · 仕入先 ${po.supplierName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto space-y-4 pr-1">
          <FulfillmentSteps status={po.status} />

          {!editable && (
            <p className="text-[11px] text-[var(--text-muted)] rounded-md border border-dashed border-[var(--border)] px-3 py-2">
              {po.status === 'pending'
                ? '発注確定の後に記録できます。'
                : 'この発注は「' +
                  PURCHASE_ORDER_STATUS_LABEL[po.status] +
                  '」のため、記録は見るだけです。'}
            </p>
          )}

          {/* 入荷と検品 */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-[var(--text-primary)]">入荷と検品</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ins-arrived" className="text-xs">
                  入荷日
                </Label>
                <Input
                  id="ins-arrived"
                  type="date"
                  value={ins.arrivedAt ?? ''}
                  disabled={!editable}
                  onChange={(e) => setIns({ ...ins, arrivedAt: e.target.value || null })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ins-inspected-at" className="text-xs">
                  検品日
                </Label>
                <Input
                  id="ins-inspected-at"
                  type="date"
                  value={ins.inspectedAt ?? ''}
                  disabled={!editable}
                  onChange={(e) => setIns({ ...ins, inspectedAt: e.target.value || null })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ins-inspector" className="text-xs">
                  担当者
                </Label>
                <select
                  id="ins-inspector"
                  className={`${SELECT_CLASS} w-full`}
                  value={ins.inspectorUid ?? ''}
                  disabled={!editable}
                  onChange={(e) => setIns({ ...ins, inspectorUid: e.target.value || null })}
                >
                  <option value="">（未選択）</option>
                  {ins.inspectorUid && !members.some((m) => m.uid === ins.inspectorUid) && (
                    <option value={ins.inspectorUid}>
                      {po.inspection?.inspectorName || '（担当者）'}
                    </option>
                  )}
                  {members.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ins-memo" className="text-xs">
                メモ（任意）
              </Label>
              <Textarea
                id="ins-memo"
                rows={2}
                placeholder="外箱の傷・付属品の欠品など"
                value={ins.memo ?? ''}
                disabled={!editable}
                onChange={(e) => setIns({ ...ins, memo: e.target.value })}
              />
            </div>
          </section>

          {/* 機器ごとの検品・シリアル */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-xs font-semibold text-[var(--text-primary)]">
                {isTimeWaver ? '機器ごとの検品・シリアル' : 'シリアル番号'}
              </h3>
              <span
                className={`text-xs font-semibold tabular-nums ${countDone ? 'text-[var(--accent-green)]' : 'text-[var(--accent-amber)]'}`}
              >
                {countLabel}
              </span>
            </div>

            {ins.lines.map((line, li) => {
              const item = po.lineItems[li];
              if (!item) return null;
              const lineMissing =
                line.deviceKind === null
                  ? 1
                  : line.units.reduce(
                      (n, u) => n + missingUnitFields(line.deviceKind, u).length,
                      0,
                    );
              const isNone = line.deviceKind === 'none';
              return (
                <div
                  key={li}
                  className={`rounded-md border p-3 space-y-3 ${
                    isNone
                      ? 'border-dashed border-[var(--border)] bg-[var(--bg-surface)]'
                      : lineMissing === 0
                        ? 'border-[var(--accent-green)] bg-[var(--bg-elevated)]'
                        : 'border-[var(--border)] bg-[var(--bg-elevated)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-[13px] font-medium text-[var(--text-primary)]">
                        {item.name}
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">×{item.quantity}</span>
                      {isNone ? (
                        <Badge>入力なし</Badge>
                      ) : lineMissing === 0 ? (
                        <Badge variant="approved">そろった</Badge>
                      ) : line.deviceKind === null ? (
                        <Badge variant="draft">種類を選んでください</Badge>
                      ) : (
                        <Badge variant="draft">残り {lineMissing} 項目</Badge>
                      )}
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
                      種類
                      <select
                        className={SELECT_CLASS}
                        value={line.deviceKind ?? ''}
                        disabled={!editable}
                        onChange={(e) => setLineKind(li, e.target.value as FulfillmentDeviceKind)}
                      >
                        {line.deviceKind === null && <option value="">選んでください</option>}
                        {kinds.map((k) => (
                          <option key={k} value={k}>
                            {deviceKindLabel(k, isTimeWaver)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {isNone && (
                    <p className="text-[11px] text-[var(--text-muted)]">
                      機器ではない商品（ソフト・アップグレードなど）。入力はなく、所有商品にも登録しません。
                    </p>
                  )}
                  {line.deviceKind === null && (
                    <p className="text-[11px] text-[var(--accent-amber)]">
                      商品名から種類を決められませんでした。右の「種類」から選んでください。
                    </p>
                  )}

                  {needsUnits(line.deviceKind) &&
                    line.units.map((u, ui) => {
                      const acc = accessoriesFor(line.deviceKind);
                      const unitMissing = missingUnitFields(line.deviceKind, u);
                      return (
                        <div
                          key={ui}
                          className={`space-y-2.5 ${ui > 0 ? 'border-t border-[var(--border)] pt-3' : ''}`}
                        >
                          <span className="text-xs font-semibold text-[var(--text-muted)]">
                            #{ui + 1}
                          </span>
                          {acc.length > 0 && (
                            <fieldset className="space-y-1.5">
                              <legend className="text-xs text-[var(--text-muted)] mb-1">
                                付属品
                              </legend>
                              <div className="flex flex-wrap items-center gap-2">
                                {acc.map((key) => {
                                  const checked = !!u.accessories?.[key];
                                  return (
                                    <label
                                      key={key}
                                      className={`inline-flex items-center gap-2 min-h-[36px] px-3 rounded-sm border text-xs cursor-pointer bg-[var(--bg-input)] ${
                                        checked
                                          ? 'border-[var(--accent-green)]'
                                          : 'border-[var(--accent-amber)]'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="size-4 accent-[var(--accent)]"
                                        checked={checked}
                                        disabled={!editable}
                                        onChange={(e) =>
                                          toggleAccessory(li, ui, key, e.target.checked)
                                        }
                                      />
                                      {INSPECTION_ACCESSORY_LABEL[key]}
                                    </label>
                                  );
                                })}
                                {line.deviceKind === 'tw_mobile' && (
                                  <span className="text-[11px] text-[var(--text-muted)]">
                                    電源ケーブルは付属しません
                                  </span>
                                )}
                              </div>
                            </fieldset>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">{serialFieldLabel(line.deviceKind)}</Label>
                              <Input
                                value={u.serialNumber}
                                disabled={!editable}
                                placeholder={line.deviceKind === 'dell_pc' ? '英数字 7 桁' : ''}
                                className={`font-mono text-xs ${u.serialNumber.trim() ? '' : 'border-[var(--accent-amber)]'}`}
                                onChange={(e) =>
                                  setUnit(li, ui, () => ({ serialNumber: e.target.value }))
                                }
                              />
                            </div>
                            {hasWarranty(line.deviceKind) && (
                              <>
                                <div className="space-y-1">
                                  <Label className="text-xs">保証開始日</Label>
                                  <Input
                                    type="date"
                                    value={u.warrantyStartAt ?? ''}
                                    disabled={!editable}
                                    className={
                                      u.warrantyStartAt ? '' : 'border-[var(--accent-amber)]'
                                    }
                                    onChange={(e) => changeWarrantyStart(li, ui, e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">保証終了日</Label>
                                  <Input
                                    type="date"
                                    value={u.warrantyEndAt ?? ''}
                                    disabled={!editable}
                                    className={
                                      u.warrantyEndAt ? '' : 'border-[var(--accent-amber)]'
                                    }
                                    onChange={(e) =>
                                      setUnit(li, ui, () => ({
                                        warrantyEndAt: e.target.value || null,
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            )}
                            {line.deviceKind === 'dell_pc' && (
                              <div className="space-y-1">
                                <Label className="text-xs">エクスプレスコード</Label>
                                <Input
                                  value={u.expressCode ?? ''}
                                  disabled={!editable}
                                  placeholder="000-000-000-00"
                                  className={`font-mono text-xs ${u.expressCode?.trim() ? '' : 'border-[var(--accent-amber)]'}`}
                                  onChange={(e) =>
                                    setUnit(li, ui, () => ({ expressCode: e.target.value }))
                                  }
                                />
                              </div>
                            )}
                          </div>
                          {hasWarranty(line.deviceKind) && ui === 0 && editable && (
                            <p className="text-[11px] text-[var(--text-muted)]">
                              保証開始日を入れると、保証終了日に 2
                              年後の前日が入ります（変更できます）。
                            </p>
                          )}
                          {line.deviceKind === 'dell_pc' && ui === 0 && (
                            <p className="text-[11px] text-[var(--text-muted)]">
                              Dell PC
                              はサービスタグを機器の番号として使います（シリアル番号の欄はありません）。
                            </p>
                          )}
                          {unitMissing.length > 0 && editable && (
                            <p className="text-[11px] text-[var(--accent-amber)]">
                              未入力: {unitMissing.join('・')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}

            <p className="text-[11px] text-[var(--text-muted)] rounded-md border border-[var(--border)] bg-[var(--bg-surface)] px-3 py-2">
              設置日は入力しません。所有商品の設置日には、納品完了の日が自動で入ります。
              {isTimeWaver &&
                'ここで入れた保証期間・サービスタグ・エクスプレスコードは、納品完了のときに顧客の「所有商品」へ一緒に登録されます。'}
            </p>
          </section>

          {/* 検品済のチェック */}
          <label
            className={`flex items-center gap-2.5 min-h-[44px] px-3 rounded-md border text-[13px] cursor-pointer bg-[var(--bg-elevated)] ${
              ins.inspected ? 'border-[var(--accent-green)]' : 'border-[var(--accent-amber)]'
            }`}
          >
            <input
              type="checkbox"
              className="size-[18px] accent-[var(--accent)]"
              checked={ins.inspected}
              disabled={!editable}
              onChange={(e) =>
                setIns({
                  ...ins,
                  inspected: e.target.checked,
                  inspectedAt: e.target.checked ? (ins.inspectedAt ?? todayYmd()) : ins.inspectedAt,
                })
              }
            />
            検品済（外観・付属品・動作を確認した）
          </label>

          {editable && (
            <div
              className={`rounded-md border px-3 py-2 text-xs leading-relaxed ${
                progress.ready
                  ? 'border-[var(--accent-green)] text-[var(--accent-green)] bg-[var(--accent-green-subtle)]'
                  : 'border-[var(--accent-amber)] text-[var(--accent-amber)] bg-[var(--accent-amber-subtle)]'
              }`}
            >
              {progress.ready ? (
                'すべてそろいました。保存すると「検品済（発送待ち）」になり、顧客へ発送できます。'
              ) : (
                <>
                  「顧客へ発送」は、検品済のチェックと、
                  {isTimeWaver
                    ? '機器ごとの付属品・シリアル番号・保証期間（Dell PC はサービスタグ・エクスプレスコード）'
                    : '台数分のシリアル番号'}
                  がそろうと押せます。途中でも保存できます。
                  <br />
                  <b>
                    残り：{shownMissing.join(' / ')}
                    {moreMissing > 0 ? ` ほか ${moreMissing} 件` : ''}
                  </b>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            {editable ? 'キャンセル' : '閉じる'}
          </Button>
          {editable && (
            <>
              <Button
                variant={progress.ready && onProceedToShip ? 'secondary' : 'primary'}
                onClick={() => handleSave(false)}
                disabled={save.isPending}
              >
                {save.isPending ? '保存中…' : '保存'}
              </Button>
              {progress.ready && onProceedToShip && (
                <Button
                  variant="primary"
                  onClick={() => handleSave(true)}
                  disabled={save.isPending}
                >
                  <Truck className="size-4" />
                  保存して顧客へ発送へ進む
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
